/**
 * Webflow embed loader — the single classic script the Webflow site includes
 * (see .deploy/webflow-embed.html). The staged preview and the released site
 * are one Webflow publish sharing identical markup, so this loader forks
 * between the environments at runtime, keyed on the hostname, and injects the
 * bundle tags from the matching API host with explicit runtime configuration
 * (data-uc-* attributes, read by the SDK's runtime-config).
 *
 * The loader itself is always fetched from the production host — one publish
 * means one URL for both environments — so changes here reach the staged
 * preview only with a production release: keep it minimal and stable.
 *
 * Cache-busting is handled entirely by HTTP headers and the build output
 * (see .deploy/nginx-assets-cors.conf): entry bundles keep stable names and
 * are revalidated via ETag on every load, their code-split chunks carry
 * content hashes and are cached immutably. The loader must NOT append any
 * query parameter to the bundle URLs — a query string changes the ESM module
 * identity of the entry while its chunks re-import it by its bare name,
 * which makes the browser execute the whole entry module twice — two Swiper
 * instances fighting over the challenge slideshow, doubled status polling
 * tripping the rate limiter.
 *
 * The widgets stylesheet is primarily loaded by the embed itself: this loader
 * runs deferred, after the page is parsed, so a <link> injected here can
 * never block the first paint — the embed's inline script document.write()s
 * the render-blocking link instead (see .deploy/webflow-embed.html). The
 * loadStyle call below stays as a safety net for pages missing that embed;
 * the duplicate resolves from cache.
 *
 * The constants deliberately duplicate the SDK's URLS map and
 * STAGED_PREVIEW_HOST_SUFFIX (packages/sdk/src/{config,runtime-config}.ts):
 * importing them would pull the SDK's module-scope route table into this
 * bundle, and they are infrastructure constants that appear verbatim in the
 * nginx configs anyway.
 */

const STAGED_PREVIEW_HOST_SUFFIX = '.webflow.io';
const STAGING_HOST = 'https://stage.urbanchallenger.com';
const PRODUCTION_HOST = 'https://api.urbanchallenger.com';

const isStagedPreview = window.location.hostname.endsWith(
    STAGED_PREVIEW_HOST_SUFFIX
);
const environment = isStagedPreview ? 'staging' : 'production';
const assetHost = isStagedPreview ? STAGING_HOST : PRODUCTION_HOST;

/**
 * All bundles are ESM-only, and the loader itself is built against the same
 * module-capable browser baseline. Degradation on extinct browsers: pre-ES2015
 * engines fail to parse the loader (SyntaxError), engines that parse ES2015
 * but lack module support hit this guard and load nothing — either way, no
 * broken half-loaded state. The loader stays a classic script only so the
 * embed works without `type="module"` on the tag.
 */
const supportsEsm = 'noModule' in HTMLScriptElement.prototype;

function loadBundle(path: string): void {
    if (!supportsEsm) {
        return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = assetHost + path;
    script.setAttribute('data-uc-env', environment);
    script.setAttribute('data-uc-api-base', assetHost);
    document.head.appendChild(script);
}

function loadStyle(path: string): void {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = assetHost + path;
    document.head.appendChild(link);
}

loadStyle('/assets/widgets/esm/widgets.css');
loadBundle('/assets/widgets/esm/widgets.js');
// Web components
loadBundle('/assets/components/components.js');
// Memberstack signup & redirect handling
loadBundle('/assets/widgets/esm/navigation/navigation.esm.js');
// UTM & ad-click parameter stickiness
loadBundle('/assets/widgets/esm/sticky-parameters/sticky-parameters.esm.js');

declare global {
    interface Window {
        ucLoadBundle: typeof loadBundle;
        ucLoadStyle: typeof loadStyle;
    }
}

// Exposed for ad-hoc page-level additions in Webflow custom code.
window.ucLoadBundle = loadBundle;
window.ucLoadStyle = loadStyle;

export {};
